"use client";

import * as React from "react";
import {
  BugIcon,
  CheckCircleIcon,
  HelpCircleIcon,
  SendIcon,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

// Define form schema
const formSchema = z.object({
  issueType: z.enum(["bug", "feature", "question", "other"], {
    required_error: "Please select an issue type",
  }),
  priority: z.enum(["low", "medium", "high", "urgent"], {
    required_error: "Please select a priority level",
  }),
  title: z.string().min(5, {
    message: "Title must be at least 5 characters",
  }),
  description: z.string().min(10, {
    message: "Description must be at least 10 characters",
  }),
  steps: z.string().optional(),
  browser: z.string().optional(),
  email: z.string().email({
    message: "Please enter a valid email address",
  }),
});

type FormValues = z.infer<typeof formSchema>;

export function HelpCenterSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      issueType: "bug",
      priority: "medium",
      title: "",
      description: "",
      steps: "",
      browser: "",
      email: "",
    },
  });

  async function onSubmit(data: FormValues) {
    setIsLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/help-center`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            title: data.title,
            description: `${data.description}${data.steps ? "\n\nSteps to reproduce:\n" + data.steps : ""}${data.browser ? "\n\nBrowser/Environment: " + data.browser : ""}`,
            category: data.issueType,
            priority: data.priority,
            email: data.email,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || "Failed to submit help request");
      }

      const result = await response.json();

      // Show success toast
      toast.success(
        "Help request submitted successfully! We'll get back to you soon.",
      );

      // Show success state
      setIsSubmitted(true);

      // Reset after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        form.reset();
        onOpenChange(false);
      }, 3000);
    } catch (error) {
      console.error("Help center submission error:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to submit help request. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-full max-w-md border-r sm:max-w-lg"
      >
        <SheetHeader className="space-y-2 pr-6">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <HelpCircleIcon className="size-5" />
            Help Center
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-5rem)] pr-4">
          {isSubmitted ? (
            <div className="mt-8 flex flex-col items-center justify-center text-center">
              <div className="rounded-full bg-green-100 p-3 dark:bg-green-900">
                <CheckCircleIcon className="size-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="mt-4 text-lg font-medium">
                Issue Submitted Successfully
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Thank you for your feedback. Our team will review your issue and
                get back to you soon.
              </p>
            </div>
          ) : (
            <div className="mt-6 space-y-6 px-2">
              <div className="space-y-2">
                <h3 className="text-lg font-medium">Submit an Issue</h3>
                <p className="text-sm text-muted-foreground">
                  Use this form to report bugs, request features, or ask
                  questions about the platform.
                </p>
              </div>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={form.control}
                    name="issueType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Issue Type</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            className="flex flex-wrap gap-4"
                          >
                            <FormItem className="flex items-center space-x-2 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="bug" />
                              </FormControl>
                              <FormLabel className="font-normal">
                                Bug Report
                              </FormLabel>
                            </FormItem>
                            <FormItem className="flex items-center space-x-2 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="feature" />
                              </FormControl>
                              <FormLabel className="font-normal">
                                Feature Request
                              </FormLabel>
                            </FormItem>
                            <FormItem className="flex items-center space-x-2 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="question" />
                              </FormControl>
                              <FormLabel className="font-normal">
                                Question
                              </FormLabel>
                            </FormItem>
                            <FormItem className="flex items-center space-x-2 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="other" />
                              </FormControl>
                              <FormLabel className="font-normal">
                                Other
                              </FormLabel>
                            </FormItem>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Priority</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select priority" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Issue Title</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Brief summary of the issue"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Please provide a detailed description of the issue"
                            className="min-h-[120px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="steps"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Steps to Reproduce</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="If applicable, list the steps to reproduce the issue"
                            className="min-h-[80px]"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Only required for bug reports
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="browser"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Browser / Environment</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., Chrome 98, Firefox 97, Safari 15"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Only required for bug reports
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Your Email</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="email@example.com"
                            type="email"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          We'll use this to follow up with you
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end space-x-4 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                      <SendIcon className="mr-2 size-4" />
                      {isLoading ? "Submitting..." : "Submit Issue"}
                    </Button>
                  </div>
                </form>
              </Form>

              <div className="mt-8 space-y-4">
                <h3 className="text-lg font-medium">Common Issues</h3>
                <div className="grid gap-4">
                  <Card className="p-4">
                    <div className="flex items-start gap-3">
                      <BugIcon className="mt-0.5 size-5 text-amber-500" />
                      <div>
                        <h4 className="font-medium">Can't upload documents</h4>
                        <p className="text-sm text-muted-foreground">
                          Make sure your documents are in a supported format
                          (PDF, DOCX, XLSX) and under 10MB.
                        </p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-start gap-3">
                      <BugIcon className="mt-0.5 size-5 text-amber-500" />
                      <div>
                        <h4 className="font-medium">Workflow not saving</h4>
                        <p className="text-sm text-muted-foreground">
                          Try refreshing the page and ensure all required fields
                          are completed before saving.
                        </p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-start gap-3">
                      <BugIcon className="mt-0.5 size-5 text-amber-500" />
                      <div>
                        <h4 className="font-medium">Data extraction errors</h4>
                        <p className="text-sm text-muted-foreground">
                          Check that your document is clear and properly
                          formatted. Try using a different document if issues
                          persist.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
